/**
 * Brevo Email Service
 * 
 * This service handles email sending via Brevo (formerly Sendinblue) API.
 * 
 * Setup Instructions:
 * 1. Sign up for Brevo at https://www.brevo.com/
 * 2. Get your API key from Settings > SMTP & API > API Keys
 * 3. Add your API key to environment variables as VITE_BREVO_API_KEY
 * 4. Verify your sender email/domain in Brevo dashboard
 */

export interface BrevoEmailParams {
  to: Array<{ email: string; name?: string }>;
  subject: string;
  htmlContent?: string;
  textContent?: string;
  sender?: { email: string; name?: string };
  replyTo?: { email: string; name?: string };
  cc?: Array<{ email: string; name?: string }>;
  bcc?: Array<{ email: string; name?: string }>;
  attachment?: Array<{
    name: string;
    content: string; // Base64 encoded
  }>;
  templateId?: number;
  params?: Record<string, any>; // Template variables
}

export interface BrevoEmailResponse {
  messageId: string;
  success: boolean;
  error?: string;
}

class BrevoEmailService {
  private apiKey: string;
  private apiUrl = 'https://api.brevo.com/v3/smtp/email';
  private defaultSender = {
    email: 'no-reply@clickandclaim.eu', // Change this to your verified sender
    name: 'Magic Links'
  };

  constructor() {
    // Get API key from environment variable
    this.apiKey = import.meta.env.VITE_BREVO_API_KEY || '';
    
    if (!this.apiKey) {
      // API key not configured - will use development mode
    }
  }

  /**
   * Send a simple email
   */
  async sendEmail(params: BrevoEmailParams): Promise<BrevoEmailResponse> {
    if (!this.apiKey) {
      return this.developmentModeFallback(params);
    }

    try {
      const payload: any = {
        sender: params.sender || this.defaultSender,
        to: params.to,
        subject: params.subject,
      };

      // Add content (HTML or text)
      if (params.htmlContent) {
        payload.htmlContent = params.htmlContent;
      }
      if (params.textContent) {
        payload.textContent = params.textContent;
      }

      // Add optional fields
      if (params.replyTo) payload.replyTo = params.replyTo;
      if (params.cc) payload.cc = params.cc;
      if (params.bcc) payload.bcc = params.bcc;
      if (params.attachment) payload.attachment = params.attachment;

      // Template support
      if (params.templateId) {
        payload.templateId = params.templateId;
        if (params.params) {
          payload.params = params.params;
        }
      }

      console.log('Sending email via Brevo API...', {
        to: params.to,
        subject: params.subject,
        apiUrl: this.apiUrl,
        payloadSize: JSON.stringify(payload).length,
        hasHtmlContent: !!params.htmlContent,
        hasTextContent: !!params.textContent
      });
      
      console.log('Full payload:', JSON.stringify(payload, null, 2));

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'api-key': this.apiKey,
          'content-type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('Brevo API response status:', response.status);

      const data = await response.json();
      console.log('Brevo API response data:', data);

      if (!response.ok) {
        throw new Error(data.message || `Brevo API error: ${response.status}`);
      }

      return {
        messageId: data.messageId || '',
        success: true
      };
    } catch (error: any) {
      console.error('Brevo email send error:', {
        message: error?.message,
        status: error?.status,
        response: error?.response,
        stack: error?.stack
      });
      
      let errorMessage = 'Unknown error occurred';
      
      // Handle specific error types
      if (error?.message === 'Failed to fetch') {
        errorMessage = 'Cannot reach Brevo API. This might be a CORS issue or network problem. For development, the email activation link will be logged to console instead.';
        console.warn('⚠️ CORS/Network Error - Cannot reach Brevo API');
        console.log('💡 Workaround: Use "Initial Password" method instead of email activation');
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      return {
        messageId: '',
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Send a welcome email
   */
  async sendWelcomeEmail(
    toEmail: string,
    toName: string
  ): Promise<BrevoEmailResponse> {
    return this.sendEmail({
      to: [{ email: toEmail, name: toName }],
      subject: 'Welcome to Our Platform!',
      htmlContent: `
        <html>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
              <h1 style="color: #4F46E5;">Welcome, ${toName}! 🎉</h1>
              <p>Thank you for joining our platform. We're excited to have you on board!</p>
              <p>Here's what you can do next:</p>
              <ul>
                <li>Complete your profile</li>
                <li>Explore our features</li>
                <li>Connect with other users</li>
              </ul>
              <div style="margin-top: 30px; padding: 15px; background-color: #F3F4F6; border-radius: 8px;">
                <p style="margin: 0;">Need help? Contact us at support@yourdomain.com</p>
              </div>
            </div>
          </body>
        </html>
      `,
      textContent: `Welcome, ${toName}!\n\nThank you for joining our platform. We're excited to have you on board!`
    });
  }

  /**
   * Send a notification email
   */
  async sendNotificationEmail(
    toEmail: string,
    toName: string,
    title: string,
    message: string
  ): Promise<BrevoEmailResponse> {
    return this.sendEmail({
      to: [{ email: toEmail, name: toName }],
      subject: title,
      htmlContent: `
        <html>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
              <h2 style="color: #4F46E5;">${title}</h2>
              <p>${message}</p>
              <hr style="border: none; border-top: 1px solid #E5E7EB; margin: 20px 0;">
              <p style="color: #6B7280; font-size: 12px;">
                This is an automated notification. Please do not reply to this email.
              </p>
            </div>
          </body>
        </html>
      `,
      textContent: `${title}\n\n${message}`
    });
  }

  /**
   * Send advertisement approval notification
   */
  async sendAdApprovalEmail(
    partnerEmail: string,
    partnerName: string,
    adName: string,
    approved: boolean,
    rejectionReason?: string
  ): Promise<BrevoEmailResponse> {
    const status = approved ? 'Approved' : 'Rejected';
    const statusColor = approved ? '#10B981' : '#EF4444';
    const emoji = approved ? '✅' : '❌';

    let htmlContent = `
      <html>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
          <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
            <h1 style="color: ${statusColor};">${emoji} Advertisement ${status}</h1>
            <p>Hello ${partnerName},</p>
            <p>Your advertisement "<strong>${adName}</strong>" has been <strong>${status.toLowerCase()}</strong>.</p>
    `;

    if (!approved && rejectionReason) {
      htmlContent += `
        <div style="margin: 20px 0; padding: 15px; background-color: #FEE2E2; border-left: 4px solid #EF4444; border-radius: 4px;">
          <p style="margin: 0; color: #991B1B;"><strong>Rejection Reason:</strong></p>
          <p style="margin: 5px 0 0 0; color: #991B1B;">${rejectionReason}</p>
        </div>
        <p>Please make the necessary changes and resubmit your advertisement for review.</p>
      `;
    } else if (approved) {
      htmlContent += `
        <div style="margin: 20px 0; padding: 15px; background-color: #D1FAE5; border-left: 4px solid #10B981; border-radius: 4px;">
          <p style="margin: 0; color: #065F46;">Your advertisement is now live and will be displayed in marketing campaigns!</p>
        </div>
      `;
    }

    htmlContent += `
            <p>Best regards,<br>Magic Links Admin Team</p>
          </div>
        </body>
      </html>
    `;

    return this.sendEmail({
      to: [{ email: partnerEmail, name: partnerName }],
      subject: `Advertisement ${status}: ${adName}`,
      htmlContent,
      textContent: `Advertisement ${status}\n\nHello ${partnerName},\n\nYour advertisement "${adName}" has been ${status.toLowerCase()}.${
        !approved && rejectionReason ? `\n\nRejection Reason: ${rejectionReason}` : ''
      }`
    });
  }

  /**
   * Development mode fallback - simulates email sending
   */
  private developmentModeFallback(params: BrevoEmailParams): BrevoEmailResponse {
    console.log('📧 [DEV MODE] Email would be sent:', {
      to: params.to,
      subject: params.subject,
      htmlContent: params.htmlContent?.substring(0, 100) + '...'
    });
    
    console.warn('⚠️ Email not actually sent - Brevo API key not configured');
    console.log('💡 To enable real email sending:');
    console.log('   1. Get API key from https://app.brevo.com/settings/keys/api');
    console.log('   2. Add to .env.local: VITE_BREVO_API_KEY=your_key_here');
    console.log('   3. Restart the dev server');
    
    return {
      messageId: 'dev-mode-' + Date.now(),
      success: true,
      error: undefined
    };
  }

  /**
   * Test the Brevo connection
   */
  async testConnection(testEmail: string): Promise<BrevoEmailResponse> {
    return this.sendEmail({
      to: [{ email: testEmail, name: 'Test User' }],
      subject: 'Brevo Email Service Test',
      htmlContent: `
        <html>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
              <h1 style="color: #4F46E5;">🎉 Brevo Email Test Successful!</h1>
              <p>If you're reading this, your Brevo email integration is working correctly!</p>
              <div style="margin: 20px 0; padding: 15px; background-color: #F3F4F6; border-radius: 8px;">
                <p style="margin: 0;"><strong>Test Details:</strong></p>
                <ul style="margin: 10px 0;">
                  <li>Service: Brevo (Sendinblue)</li>
                  <li>Status: ✅ Connected</li>
                  <li>Timestamp: ${new Date().toLocaleString()}</li>
                </ul>
              </div>
              <p style="color: #10B981; font-weight: bold;">✓ Email service is ready to use!</p>
            </div>
          </body>
        </html>
      `,
      textContent: `Brevo Email Test Successful!\n\nIf you're reading this, your Brevo email integration is working correctly!\n\nTimestamp: ${new Date().toLocaleString()}`
    });
  }

  /**
   * Send campaign created notification
   */
  async sendCampaignCreatedEmail(
    recipients: Array<{ email: string; name?: string }>,
    data: any
  ): Promise<BrevoEmailResponse> {
    return this.sendEmail({
      to: recipients,
      subject: `New Campaign Created: ${data.campaign_name}`,
      htmlContent: `
        <html>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
              <div style="background-color: #4F46E5; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0;">
                <h1 style="margin: 0;">📢 New Campaign Created</h1>
              </div>
              <div style="padding: 20px; background-color: #f9fafb; border-radius: 0 0 8px 8px;">
                <h2 style="color: #4F46E5; margin-top: 0;">${data.campaign_name}</h2>
                <div style="background-color: white; padding: 15px; margin: 15px 0; border-radius: 5px;">
                  <p style="margin: 5px 0;"><strong>Partner:</strong> ${data.partner_name || 'N/A'}</p>
                  <p style="margin: 5px 0;"><strong>Status:</strong> <span style="color: #10B981;">${data.status || 'Active'}</span></p>
                  <p style="margin: 5px 0;"><strong>Created:</strong> ${new Date(data.created_at || Date.now()).toLocaleString()}</p>
                  ${data.description ? `<p style="margin: 15px 0 5px 0;"><strong>Description:</strong></p><p style="margin: 5px 0;">${data.description}</p>` : ''}
                </div>
                ${data.campaign_url ? `
                  <p style="margin-top: 20px; text-align: center;">
                    <a href="${data.campaign_url}" style="display: inline-block; padding: 12px 24px; background-color: #4F46E5; color: white; text-decoration: none; border-radius: 5px; font-weight: bold;">View Campaign</a>
                  </p>
                ` : ''}
              </div>
              <div style="text-align: center; padding: 20px; font-size: 12px; color: #666;">
                <p>This is an automated notification from your partner dashboard.</p>
              </div>
            </div>
          </body>
        </html>
      `,
      textContent: `New Campaign Created: ${data.campaign_name}\n\nPartner: ${data.partner_name || 'N/A'}\nStatus: ${data.status || 'Active'}\nCreated: ${new Date(data.created_at || Date.now()).toLocaleString()}${data.description ? `\n\nDescription: ${data.description}` : ''}`
    });
  }

  /**
   * Send campaign updated notification
   */
  async sendCampaignUpdatedEmail(
    recipients: Array<{ email: string; name?: string }>,
    data: any
  ): Promise<BrevoEmailResponse> {
    return this.sendEmail({
      to: recipients,
      subject: `Campaign Updated: ${data.campaign_name}`,
      htmlContent: `
        <html>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
              <div style="background-color: #4F46E5; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0;">
                <h1 style="margin: 0;">✏️ Campaign Updated</h1>
              </div>
              <div style="padding: 20px; background-color: #f9fafb; border-radius: 0 0 8px 8px;">
                <h2 style="color: #4F46E5; margin-top: 0;">${data.campaign_name}</h2>
                <div style="background-color: white; padding: 15px; margin: 15px 0; border-radius: 5px;">
                  <p style="margin: 5px 0;"><strong>Updated by:</strong> ${data.updated_by || 'System'}</p>
                  <p style="margin: 5px 0;"><strong>Updated at:</strong> ${new Date(data.updated_at || Date.now()).toLocaleString()}</p>
                </div>
                ${data.changes && data.changes.length > 0 ? `
                  <div style="background-color: white; padding: 15px; margin: 15px 0; border-left: 4px solid #4F46E5; border-radius: 5px;">
                    <h3 style="margin-top: 0; color: #4F46E5;">Changes Made:</h3>
                    <ul style="margin: 10px 0; padding-left: 20px;">
                      ${data.changes.map((change: string) => `<li>${change}</li>`).join('')}
                    </ul>
                  </div>
                ` : ''}
                ${data.campaign_url ? `
                  <p style="margin-top: 20px; text-align: center;">
                    <a href="${data.campaign_url}" style="display: inline-block; padding: 12px 24px; background-color: #4F46E5; color: white; text-decoration: none; border-radius: 5px; font-weight: bold;">View Campaign</a>
                  </p>
                ` : ''}
              </div>
              <div style="text-align: center; padding: 20px; font-size: 12px; color: #666;">
                <p>This is an automated notification from your partner dashboard.</p>
              </div>
            </div>
          </body>
        </html>
      `,
      textContent: `Campaign Updated: ${data.campaign_name}\n\nUpdated by: ${data.updated_by || 'System'}\nUpdated at: ${new Date(data.updated_at || Date.now()).toLocaleString()}${data.changes && data.changes.length > 0 ? `\n\nChanges:\n${data.changes.map((c: string) => `- ${c}`).join('\n')}` : ''}`
    });
  }

  /**
   * Send campaign status change notification
   */
  async sendCampaignStatusEmail(
    recipients: Array<{ email: string; name?: string }>,
    data: any
  ): Promise<BrevoEmailResponse> {
    const statusColors: Record<string, string> = {
      active: '#10B981',
      paused: '#F59E0B',
      completed: '#6B7280',
      draft: '#9CA3AF'
    };
    const statusColor = statusColors[data.status?.toLowerCase()] || '#6B7280';

    return this.sendEmail({
      to: recipients,
      subject: `Campaign Status Changed: ${data.campaign_name}`,
      htmlContent: `
        <html>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
              <div style="background-color: #4F46E5; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0;">
                <h1 style="margin: 0;">🔄 Campaign Status Changed</h1>
              </div>
              <div style="padding: 20px; background-color: #f9fafb; border-radius: 0 0 8px 8px;">
                <h2 style="color: #4F46E5; margin-top: 0;">${data.campaign_name}</h2>
                <div style="background-color: white; padding: 15px; margin: 15px 0; border-radius: 5px; text-align: center;">
                  <p style="margin: 0 0 10px 0;"><strong>New Status:</strong></p>
                  <span style="display: inline-block; padding: 8px 20px; border-radius: 20px; color: white; font-weight: bold; background-color: ${statusColor};">
                    ${(data.status || 'Unknown').toUpperCase()}
                  </span>
                  ${data.previous_status ? `<p style="margin: 15px 0 0 0; color: #6B7280;">Previous: ${data.previous_status}</p>` : ''}
                </div>
                <div style="background-color: white; padding: 15px; margin: 15px 0; border-radius: 5px;">
                  <p style="margin: 5px 0;"><strong>Changed at:</strong> ${new Date(data.updated_at || Date.now()).toLocaleString()}</p>
                  ${data.reason ? `<p style="margin: 15px 0 5px 0;"><strong>Reason:</strong></p><p style="margin: 5px 0;">${data.reason}</p>` : ''}
                </div>
                ${data.campaign_url ? `
                  <p style="margin-top: 20px; text-align: center;">
                    <a href="${data.campaign_url}" style="display: inline-block; padding: 12px 24px; background-color: #4F46E5; color: white; text-decoration: none; border-radius: 5px; font-weight: bold;">View Campaign</a>
                  </p>
                ` : ''}
              </div>
              <div style="text-align: center; padding: 20px; font-size: 12px; color: #666;">
                <p>This is an automated notification from your partner dashboard.</p>
              </div>
            </div>
          </body>
        </html>
      `,
      textContent: `Campaign Status Changed: ${data.campaign_name}\n\nNew Status: ${data.status || 'Unknown'}${data.previous_status ? `\nPrevious Status: ${data.previous_status}` : ''}\nChanged at: ${new Date(data.updated_at || Date.now()).toLocaleString()}${data.reason ? `\n\nReason: ${data.reason}` : ''}`
    });
  }

  /**
   * Send promo code request notification
   */
  async sendPromoCodeRequestEmail(
    recipients: Array<{ email: string; name?: string }>,
    data: any
  ): Promise<BrevoEmailResponse> {
    return this.sendEmail({
      to: recipients,
      subject: `New Promo Code Request`,
      htmlContent: `
        <html>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
              <div style="background-color: #4F46E5; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0;">
                <h1 style="margin: 0;">🎟️ New Promo Code Request</h1>
              </div>
              <div style="padding: 20px; background-color: #f9fafb; border-radius: 0 0 8px 8px;">
                <div style="background-color: white; padding: 15px; margin: 15px 0; border-radius: 5px;">
                  <p style="margin: 5px 0;"><strong>Requested by:</strong> ${data.user_name || 'Unknown'} (${data.user_email || 'N/A'})</p>
                  <p style="margin: 5px 0;"><strong>Campaign:</strong> ${data.campaign_name || 'N/A'}</p>
                  <p style="margin: 5px 0;"><strong>Requested at:</strong> ${new Date(data.requested_at || Date.now()).toLocaleString()}</p>
                  ${data.message ? `<p style="margin: 15px 0 5px 0;"><strong>Message:</strong></p><p style="margin: 5px 0; padding: 10px; background-color: #F3F4F6; border-radius: 5px;">${data.message}</p>` : ''}
                </div>
                ${data.request_url ? `
                  <p style="margin-top: 20px; text-align: center;">
                    <a href="${data.request_url}" style="display: inline-block; padding: 12px 24px; background-color: #4F46E5; color: white; text-decoration: none; border-radius: 5px; font-weight: bold;">Review Request</a>
                  </p>
                ` : ''}
              </div>
              <div style="text-align: center; padding: 20px; font-size: 12px; color: #666;">
                <p>This is an automated notification from your partner dashboard.</p>
              </div>
            </div>
          </body>
        </html>
      `,
      textContent: `New Promo Code Request\n\nRequested by: ${data.user_name || 'Unknown'} (${data.user_email || 'N/A'})\nCampaign: ${data.campaign_name || 'N/A'}\nRequested at: ${new Date(data.requested_at || Date.now()).toLocaleString()}${data.message ? `\n\nMessage: ${data.message}` : ''}`
    });
  }

  /**
   * Send advertisement created notification
   */
  async sendAdvertisementCreatedEmail(
    recipients: Array<{ email: string; name?: string }>,
    data: any
  ): Promise<BrevoEmailResponse> {
    return this.sendEmail({
      to: recipients,
      subject: `New Advertisement Created`,
      htmlContent: `
        <html>
          <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
              <div style="background-color: #4F46E5; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0;">
                <h1 style="margin: 0;">🎨 New Advertisement Created</h1>
              </div>
              <div style="padding: 20px; background-color: #f9fafb; border-radius: 0 0 8px 8px;">
                <h2 style="color: #4F46E5; margin-top: 0;">${data.ad_title || data.ad_name || 'New Advertisement'}</h2>
                <div style="background-color: white; padding: 15px; margin: 15px 0; border-radius: 5px;">
                  <p style="margin: 5px 0;"><strong>Status:</strong> <span style="color: #F59E0B;">Pending Approval</span></p>
                  <p style="margin: 5px 0;"><strong>Created:</strong> ${new Date(data.created_at || Date.now()).toLocaleString()}</p>
                </div>
                ${data.ad_image_url ? `
                  <div style="background-color: white; padding: 15px; margin: 15px 0; border-radius: 5px; text-align: center;">
                    <img src="${data.ad_image_url}" alt="Advertisement" style="max-width: 100%; height: auto; border-radius: 5px;">
                  </div>
                ` : ''}
                ${data.ad_url ? `
                  <p style="margin-top: 20px; text-align: center;">
                    <a href="${data.ad_url}" style="display: inline-block; padding: 12px 24px; background-color: #4F46E5; color: white; text-decoration: none; border-radius: 5px; font-weight: bold;">Review Advertisement</a>
                  </p>
                ` : ''}
              </div>
              <div style="text-align: center; padding: 20px; font-size: 12px; color: #666;">
                <p>This is an automated notification from your partner dashboard.</p>
              </div>
            </div>
          </body>
        </html>
      `,
      textContent: `New Advertisement Created\n\nTitle: ${data.ad_title || data.ad_name || 'New Advertisement'}\nStatus: Pending Approval\nCreated: ${new Date(data.created_at || Date.now()).toLocaleString()}`
    });
  }
}

export const brevoEmailService = new BrevoEmailService();
